import { useEffect, useState } from 'react';
import {
  i18n,
  orderEmailPreferenceKeys,
  type OrderEmailPreferenceKey,
  type OrderEmailPreferences,
} from '@wms/shared';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth-context.js';

const { t } = i18n;

export function OrderNotificationSettingsPage() {
  const { user } = useAuth();
  const canManage = user?.roles.some((r) => r.role === 'ADMIN' || r.role === 'OPERATOR');
  return (
    <div className="space-y-8">
      <OrderEmailPreferencesEditor />
      {canManage ? <NotificationRecipients /> : null}
    </div>
  );
}

function OrderEmailPreferencesEditor({
  endpoint = '/settings/order-notifications',
  initial,
  onSaved,
}: {
  endpoint?: string;
  initial?: OrderEmailPreferences;
  onSaved?: (preferences: OrderEmailPreferences) => void;
}) {
  const [preferences, setPreferences] = useState<OrderEmailPreferences | null>(initial ?? null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (initial) return;
    let active = true;
    api<OrderEmailPreferences>(endpoint)
      .then((result) => {
        if (active) setPreferences(result);
      })
      .catch((err: unknown) => {
        if (active) setError(String(err));
      });
    return () => {
      active = false;
    };
  }, [endpoint, initial]);

  function toggle(eventKey: OrderEmailPreferenceKey) {
    setSaved(false);
    setPreferences((current) =>
      current ? { ...current, [eventKey]: !current[eventKey] } : current,
    );
  }

  async function save() {
    if (!preferences) return;
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const updated = await api<OrderEmailPreferences>(endpoint, {
        method: 'PUT',
        body: preferences,
      });
      setPreferences(updated);
      onSaved?.(updated);
      setSaved(true);
    } catch (err) {
      setError(String(err));
    } finally {
      setSaving(false);
    }
  }

  if (error && !preferences) return <div className="text-sm text-red-600">{error}</div>;
  if (!preferences) {
    return <div className="text-sm text-muted-foreground">{t('common.loading')}</div>;
  }

  return (
    <section className="mx-auto max-w-2xl space-y-4">
      <header>
        <h2 className="text-xl font-semibold">{t('notifications.order.title')}</h2>
        {!initial ? (
          <p className="mt-1 text-sm text-muted-foreground">{t('notifications.order.intro')}</p>
        ) : null}
      </header>

      <div className="overflow-hidden rounded-md border border-border">
        {orderEmailPreferenceKeys.map((eventKey) => {
          const enabled = preferences[eventKey];
          return (
            <div
              key={eventKey}
              className="flex items-center justify-between gap-4 border-b border-border px-4 py-3 last:border-b-0"
            >
              <span className="text-sm font-medium">{t(`notifications.event.${eventKey}`)}</span>
              <button
                type="button"
                role="switch"
                aria-checked={enabled}
                data-event-key={eventKey}
                onClick={() => toggle(eventKey)}
                disabled={saving}
                className={`min-w-24 rounded-full border px-3 py-1 text-xs font-medium ${
                  enabled
                    ? 'border-emerald-300 bg-emerald-100 text-emerald-900 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-100'
                    : 'border-border bg-muted text-muted-foreground'
                }`}
              >
                {enabled ? t('notifications.enabled') : t('notifications.disabled')}
              </button>
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-end gap-3">
        {error ? <span className="text-sm text-red-600">{t('common.error')}</span> : null}
        {saved ? (
          <span className="text-sm text-emerald-700 dark:text-emerald-300">
            {t('notifications.saved')}
          </span>
        ) : null}
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="rounded bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50"
        >
          {saving ? t('common.loading') : t('common.save')}
        </button>
      </div>
    </section>
  );
}

interface Recipient {
  id: string;
  displayName: string;
  email: string;
  preferences: OrderEmailPreferences;
}

function NotificationRecipients() {
  const [recipients, setRecipients] = useState<Recipient[] | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    api<Recipient[]>('/settings/notification-recipients')
      .then((rows) => {
        if (active) setRecipients(rows);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);
  const selected = recipients?.find((row) => row.id === selectedId);
  return (
    <section className="mx-auto max-w-2xl space-y-4" aria-label={t('notifications.manage.title')}>
      <h2 className="text-xl font-semibold">{t('notifications.manage.title')}</h2>
      <p className="text-sm text-muted-foreground">{t('notifications.manage.intro')}</p>
      {failed ? (
        <p role="alert">{t('common.error')}</p>
      ) : !recipients ? (
        <p>{t('common.loading')}</p>
      ) : recipients.length === 0 ? (
        <p>{t('notifications.manage.empty')}</p>
      ) : (
        <label className="block space-y-1">
          <span>{t('notifications.manage.recipient')}</span>
          <select
            className="w-full rounded border border-border bg-background p-2"
            value={selectedId}
            onChange={(event) => setSelectedId(event.target.value)}
          >
            <option value="">{t('notifications.manage.select')}</option>
            {recipients.map((recipient) => (
              <option key={recipient.id} value={recipient.id}>
                {recipient.displayName} ({recipient.email})
              </option>
            ))}
          </select>
        </label>
      )}
      {selected ? (
        <OrderEmailPreferencesEditor
          key={selected.id}
          endpoint={`/settings/notification-recipients/${selected.id}`}
          initial={selected.preferences}
          onSaved={(preferences) =>
            setRecipients(
              (rows) =>
                rows?.map((row) => (row.id === selected.id ? { ...row, preferences } : row)) ??
                null,
            )
          }
        />
      ) : null}
    </section>
  );
}
